import LegalLayout from "./LegalLayout";

// Effective date is bumped whenever PRIVACY.md is materially updated. The
// markdown file at the repo root is the canonical source; this page should
// match it semantically. If you change one, change the other.
const EFFECTIVE_DATE = "May 4, 2026";
const CONTACT_EMAIL = "support@questscheduler.cc";

export default function PrivacyPage() {
  return (
    <LegalLayout title="Privacy Policy">
      <p>
        Effective date: <strong>{EFFECTIVE_DATE}</strong>
      </p>

      <p>
        Quest Scheduler (“we”, “us”, “our”) provides tools for scheduling tabletop sessions,
        collecting votes, detecting calendar conflicts, and creating calendar events. This
        Privacy Policy explains what information we collect, how we use it, how it is shared
        (only with your explicit consent), and your choices.
      </p>

      <h2>Information we collect</h2>
      <ul>
        <li>
          <strong>Account information.</strong> When you create an account or sign in, we
          receive your email address, display name, profile photo, provider identifiers (such
          as Google or Discord IDs), and an internal user ID. Authentication credentials are
          handled by Firebase Authentication.
        </li>
        <li>
          <strong>Profile identifiers.</strong> We store your display name and any identifiers
          you choose to use, such as a Quest Scheduler username, a Discord username, or your
          email. We also store normalized (lowercase) versions to support lookups for invites
          and blocking.
        </li>
        <li>
          <strong>Profile photos and avatars.</strong> We store provider profile photos (such
          as Google) and Discord avatar metadata if Discord is linked. If you upload a custom
          avatar, we store the image in Firebase Storage and store a public URL in your
          profile. Avatars are public so other users can see them.
        </li>
        <li>
          <strong>Discord account and server information.</strong> If you sign in with or link
          Discord, we receive your Discord user ID, username, display name, avatar metadata,
          and (for Discord login) a verified email address. If you connect a server/channel,
          we receive the IDs/names for the linked guild, channel, and any configured notify
          role.
        </li>
        <li>
          <strong>Google Calendar access (opt-in).</strong> If you choose to link a Google
          calendar, we request OAuth scopes that let us read events from the calendars you
          select and create events you approve. We read event metadata so we can detect
          conflicts with proposed session time slots and so you can opt to share your busy
          windows with friends (see “Calendar event cache” below). Linking a calendar is
          optional; the app works without one, with reduced features.
        </li>
        <li>
          <strong>Calendar event cache.</strong> When you link a calendar, we store a{" "}
          <strong>structural-only</strong> copy of each event in our database. The cache
          holds the times, opaque identifiers, and your RSVP status that the app needs to
          schedule against your calendar — and nothing else. We <strong>never</strong>{" "}
          persist event titles, descriptions, locations, or attendee lists. The full
          at-rest allowlist: <code>provider</code> (currently <code>google</code>),{" "}
          <code>calendarId</code>, <code>eventId</code> (opaque identifiers — not
          human-readable), <code>startUtc</code>, <code>endUtc</code>, <code>allDay</code>,{" "}
          <code>recurringEventId</code>, <code>originalStartTime</code>,{" "}
          <code>responseStatus</code> (your own attendance status), and{" "}
          <code>lastSyncedAt</code>. We do <strong>not</strong> cache event titles
          (<code>summary</code>), descriptions, locations, attendee email lists, organizer
          details, conferencing data, attachments, deep links (<code>htmlLink</code>), or
          any other field. Event titles you see inside the app (in conflict warnings, the
          calendar overlay, and similar surfaces) are fetched <strong>directly from your
          calendar provider by your browser, on demand</strong>, using a short-lived
          read-only access token minted by our server. Titles are held briefly in memory
          in the open browser tab and never written to disk on our side. Even if our
          database were breached, an attacker would see opaque calendar and event
          identifiers, your RSVP status, and timing — they would not see what your events
          are about.
        </li>
        <li>
          <strong>OAuth tokens and state.</strong> When you link Google Calendar, we store an
          encrypted refresh token so we can keep your event cache up-to-date and create events
          you request. For Discord login/linking, we store short-lived OAuth state and only
          the Discord identifiers needed for linking; we do not store Discord OAuth access
          tokens.
        </li>
        <li>
          <strong>User content.</strong> We store the scheduling data you create or submit,
          including session polls, time slots, votes, invites, questing groups, friend
          requests, blocked users, availability marks, and notifications.
        </li>
        <li>
          <strong>Discord interactions.</strong> We record commands, button/select interactions,
          and timestamps to process votes, prevent abuse, and keep poll state in sync. We do
          not read message content from your server.
        </li>
        <li>
          <strong>Usage data.</strong> Our infrastructure providers may log basic usage data
          such as IP address, browser type, and timestamps for security and reliability.
        </li>
      </ul>

      <h2>How we use information</h2>
      <ul>
        <li>Provide the service: scheduling, voting, conflict detection, and notifications.</li>
        <li>Authenticate users, link sign-in providers, and prevent account lockout.</li>
        <li>Create calendar events you request and keep your event cache up-to-date.</li>
        <li>
          Detect and warn about conflicts between your busy windows and proposed session
          times so you can vote with full information.
        </li>
        <li>
          Suggest “auto-vote” answers based on your availability marks and busy windows. You
          remain in full control — auto-votes can be overridden manually.
        </li>
        <li>Link your Discord account, post poll updates, and record votes from Discord.</li>
        <li>Resolve invites by email, Discord username, or Quest Scheduler username.</li>
        <li>Display public identifiers and avatars to other users for coordination and safety.</li>
        <li>Send transactional emails related to invites and updates.</li>
        <li>Maintain security, prevent abuse, and improve reliability.</li>
      </ul>

      <h2>Friend availability sharing (opt-in, per-friend)</h2>
      <p>
        You can grant a specific friend permission to see your availability when they are
        creating a session poll that you are also invited to. Sharing is{" "}
        <strong>off by default</strong>, <strong>per-friend</strong>, and{" "}
        <strong>revocable at any time</strong> from the Friends &amp; Groups page in Settings.
      </p>
      <p>
        When you grant a share, the friend who is creating a poll can see, only on that poll’s
        edit page and only for slots they are arranging with you, your busy windows
        (start/end times) and your availability marks (Preferred / Feasible blocks). If you
        toggle the “redact mark titles” option when granting the share, only the level
        (Preferred / Feasible) is shown — never the mark’s free-text title.
      </p>
      <p>
        We never share event descriptions, locations, attendee lists, organizer details, or
        any other field outside the cache allowlist above (those fields are not even stored).
        We do not share anything outside the temporal window of the poll being created (a
        fixed ~90-day band around the poll’s slots), and we do not share with anyone who is
        not actively invited to the poll. Revoking a share takes effect immediately on the
        friend’s next attempt to view your availability, and we record an audit-log entry.
      </p>

      <h2>Public profile and identifiers</h2>
      <p>
        Quest Scheduler shows a display name and a unique public identifier with your profile
        in contexts like invites, polls, and friend requests. You can choose which identifier
        is public (email, Discord username, or Quest Scheduler username) in Settings. Other
        users may use this identifier to invite or block you.
      </p>

      <h2>How we share information</h2>
      <ul>
        <li>
          <strong>Service providers.</strong> We use third‑party providers such as Firebase and
          Google APIs to host data, authenticate users, and deliver notifications.
        </li>
        <li>
          <strong>Discord.</strong> If you link Discord, we send poll updates, vote confirmations,
          and related metadata to Discord so the bot can display them in your server/channel.
          You can opt out of including conflict times or per-event titles in Discord ephemeral
          panels via Settings.
        </li>
        <li>
          <strong>Email delivery.</strong> We use an email delivery provider to send invite and
          notification emails.
        </li>
        <li>
          <strong>With other users.</strong> Your name, chosen public identifier, avatar, and
          responses may be visible to other participants in a session or group you join. If you
          use email invites or choose email as your public identifier, your email address may be
          visible to those users.
        </li>
        <li>
          <strong>With friends you have explicitly granted availability sharing to.</strong>{" "}
          See “Friend availability sharing” above.
        </li>
        <li>
          <strong>Legal requirements.</strong> We may disclose information if required by law or
          to protect our rights and users.
        </li>
      </ul>
      <p>
        We <strong>do not sell</strong> your information to advertisers or data brokers, and we
        do not use Google Calendar data for advertising.
      </p>

      <h2>Third-party policies</h2>
      <ul>
        <li>
          Google Privacy Policy:{" "}
          <a href="https://policies.google.com/privacy" target="_blank" rel="noreferrer">
            policies.google.com/privacy
          </a>
        </li>
        <li>
          Google Terms of Service:{" "}
          <a href="https://policies.google.com/terms" target="_blank" rel="noreferrer">
            policies.google.com/terms
          </a>
        </li>
        <li>
          Discord Privacy Policy:{" "}
          <a href="https://discord.com/privacy" target="_blank" rel="noreferrer">
            discord.com/privacy
          </a>
        </li>
        <li>
          Discord Terms of Service:{" "}
          <a href="https://discord.com/terms" target="_blank" rel="noreferrer">
            discord.com/terms
          </a>
        </li>
      </ul>

      <h2>Google API Services User Data Policy</h2>
      <p>
        Quest Scheduler's use and transfer of information received from Google APIs adheres to
        the Google API Services User Data Policy, including the Limited Use requirements. We
        only use Google user data to provide the features you request (conflict detection,
        calendar event creation, opt-in availability sharing). We do not use it for advertising
        and we do not sell it to third parties. We do not allow human access to Google user
        data except to provide user-requested support, for security reasons, or to comply with
        law.
      </p>
      <p>
        You can review the policy here:{" "}
        <a
          href="https://developers.google.com/terms/api-services-user-data-policy"
          target="_blank"
          rel="noreferrer"
        >
          Google API Services User Data Policy
        </a>
        .
      </p>

      <h2>Data retention</h2>
      <p>
        We retain your data for as long as your account is active or as needed to provide the
        service.
      </p>
      <ul>
        <li>
          You can delete your account in Settings, which triggers deletion of your account
          data, including polls you created, votes you submitted, identifiers, profile
          details, availability marks, the calendar event cache, and any outgoing or incoming
          sharing grants.
        </li>
        <li>
          You can delete only the calendar event cache (without unlinking) using the{" "}
          <em>Delete cached calendar events</em> action in Settings. This does not affect your
          Google Calendar — only our cached copy.
        </li>
        <li>
          If you unlink a calendar, we sweep its events and any associated overrides from your
          cache.
        </li>
        <li>
          If you revoke a sharing grant, the grant is moved to a <code>revoked</code> state and
          the friend can no longer view your availability. We retain a small audit record of
          the grant + revocation for your reference.
        </li>
        <li>Residual backups may persist for a limited time before permanent removal.</li>
      </ul>

      <h2>Your choices</h2>
      <ul>
        <li>You can access and update your profile information in your Google Account.</li>
        <li>
          You can revoke Google Calendar access from your Google account settings, and you can
          unlink any individual linked calendar in Settings.
        </li>
        <li>You can link or unlink Discord in Settings, or remove the bot from your server.</li>
        <li>You can set a display name, choose your public identifier, or create a Quest Scheduler username.</li>
        <li>You can upload a custom avatar or choose which avatar source to use.</li>
        <li>
          You can grant or revoke availability sharing per-friend on the Friends &amp; Groups
          page.
        </li>
        <li>You can delete the cached calendar events without unlinking your calendar.</li>
        <li>You can delete your account in Settings to erase your account data.</li>
      </ul>

      <h2>Access, deletion, and portability</h2>
      <p>
        You may request access to or deletion of your data by contacting us. If you delete your
        account in the app, we remove your profile and scheduling data and disassociate linked
        integrations from your profile. You can also revoke access directly with the provider.
      </p>

      <h2>Security</h2>
      <p>
        We use reasonable technical measures to protect information, including encrypted
        refresh tokens and per-resource Firestore security rules that prevent cross-user reads.
        No system is completely secure, and we cannot guarantee absolute security.
      </p>
      <p>
        For calendar data specifically, our posture is structural minimization at rest: we{" "}
        <strong>don’t store</strong> the titles, descriptions, locations, or attendee lists of
        your calendar events in our database. We store only the times, opaque identifiers, and
        RSVP status needed to schedule against your calendar (see “Calendar event cache”
        above). Event titles render in the app by being fetched directly from your calendar
        provider by your browser when needed, and are not persisted by us. A breach of our
        database would expose only opaque calendar IDs and event IDs — not what those events
        are about.
      </p>
      <p>
        We are honest about the limits of this protection: titles still pass through our
        calendar-sync service in memory while we receive an event from your provider’s API and
        write the structural fields to our database. We discard the sensitive fields before
        writing. Closing this last gap would require moving sync entirely into the browser and
        is not in scope today. The same posture applies to any future calendar provider we add
        (Apple, CalDAV, iCal subscriptions) — the architecture is provider-agnostic by design.
      </p>

      <h2>Children’s privacy</h2>
      <p>
        Quest Scheduler is not intended for children under 13. If you believe a child has
        provided personal information, please contact us.
      </p>

      <h2>Changes to this policy</h2>
      <p>
        We may update this Privacy Policy from time to time. We will update the effective date
        above and, if changes are material, provide a notice in the app.
      </p>

      <h2>Contact</h2>
      <p>
        If you have questions about this Privacy Policy, contact us at{" "}
        <a href={`mailto:${CONTACT_EMAIL}`}>{CONTACT_EMAIL}</a>.
      </p>
    </LegalLayout>
  );
}
